// The first four steps of any "add a vehicle" flow: Make → Model → Variant →
// Year, plus the model-code search that short-circuits all four
// (SOLD_VEHICLE_WIZARD_PLAN.md §B1).
//
// EXTRACTED FROM VehiclePickerModal, not copied. Two wizards need to identify a
// vehicle against the carmakes catalogue — the in-stock one that has existed for
// months, and the sold/reserved one being built — and a second copy of the
// fetch-and-pick logic would drift the moment either changed. The picker still
// owns everything from step 5 on; this owns steps 1-4 and nothing else.
//
// The hook is separate from the renderer because the PARENT owns the step number.
// The picker runs 6 or 7 steps, the sold-vehicle wizard runs 12, and both need
// their own indicator and Back button to stay in charge. So the hook takes an
// `onAdvance(step)` callback rather than setting a step itself.

import { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { apiFetch } from "../api.js";
import { mapFuelType, mapTransmission, mapDriveType, sanitizeCode } from "../carmakesMap";

// ── Shared presentational bits ───────────────────────────────────────────────

export const getMakeLogoUrl = (make) => {
  if (make.logo_url) return make.logo_url;
  const slug = make.name
    .toLowerCase()
    .replace(/\s+/g, '-')        // spaces → hyphens
    .replace(/[^a-z0-9-]/g, ''); // strip special chars
  return `https://raw.githubusercontent.com/filippofilip95/car-logos-dataset/master/logos/optimized/${slug}.png`;
};

// Dot-only step indicator — scales cleanly to any step count
//
// `mb-1`, not the `mb-5` this had. These utilities resolve on BOOTSTRAP's scale,
// not Tailwind's — bootstrap-compat.css keeps Bootstrap 5's `!important`
// spacing so the 275KB dependency could be dropped without reflowing ~254
// sites — and the two diverge from step 3 up. `mb-5` reads as Tailwind's 20px
// and rendered as Bootstrap's 48px, which is why the dots sat so far off the
// question below them. Step 1 is 4px on both scales, so this line means what it
// says whichever way the shim eventually goes.
export function StepIndicator({ step, total }) {
  return (
    <div className="flex items-center gap-1 mb-1">
      {Array.from({ length: total }, (_, i) => {
        const num     = i + 1;
        const done    = step > num;
        const current = step === num;
        return (
          <div key={num} className="flex items-center gap-1 flex-1 last:flex-none">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0"
              style={
                current
                  ? { borderColor: "var(--theme-accent)", background: "var(--theme-accent-tint)", color: "var(--theme-accent-ink)" }
                  : done
                  ? { borderColor: "var(--theme-accent)", background: "var(--theme-accent)", color: "var(--theme-accent-fg)" }
                  : { borderColor: "var(--theme-line)", background: "var(--theme-surface)", color: "var(--theme-muted)" }
              }
            >
              {done ? "✓" : num}
            </div>
            {i < total - 1 && (
              <div
                className="h-0.5 flex-1 rounded"
                style={{ background: done ? "var(--theme-accent)" : "var(--theme-line)" }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, autoFocus = true }) {
  return (
    <div className="relative mb-3">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--theme-muted)" }} />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="input-t"
        style={{ paddingLeft: "2rem" }}
        autoFocus={autoFocus}
      />
    </div>
  );
}

export function VariantRow({ v }) {
  return (
    <>
      <div className="text-sm font-medium" style={{ color: "var(--theme-fg)" }}>{v.version || v.model_code || "—"}</div>
      <div className="text-xs flex flex-wrap gap-x-3 mt-0.5" style={{ color: "var(--theme-muted)" }}>
        {v.model_code      && <span className="font-mono">{v.model_code}</span>}
        {v.body_type       && <span>{v.body_type}</span>}
        {v.displacement_cc && <span>{v.displacement_cc}cc</span>}
        {v.fuel_type       && <span>{mapFuelType(v.fuel_type) || v.fuel_type}</span>}
        {v.transmission    && <span>{mapTransmission(v.transmission) || v.transmission}</span>}
        {v.driving_wheel   && <span>{mapDriveType(v.driving_wheel) || v.driving_wheel}</span>}
        {v.date_of_launch  && <span>{new Date(v.date_of_launch).getFullYear()}</span>}
      </div>
    </>
  );
}

// Hover styling is inline because these buttons are themed off CSS variables and
// there is no :hover in inline styles. Factored out so four call sites agree.
const hoverIn = (e) => {
  e.currentTarget.style.borderColor = "var(--theme-accent-border)";
  e.currentTarget.style.background  = "var(--theme-accent-tint)";
};
const hoverOut = (e) => {
  e.currentTarget.style.borderColor = "var(--theme-line)";
  e.currentTarget.style.background  = "";
};

// ── The hook ─────────────────────────────────────────────────────────────────

/**
 * Catalogue state for steps 1-4.
 * @param {(step: number) => void} onAdvance called with the step to move to when
 *        the user picks something. The parent decides what that step means.
 */
export function useVehicleIdentity(onAdvance) {
  const [makes, setMakes]       = useState([]);
  const [models, setModels]     = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState("");

  const [codeMode, setCodeMode]       = useState(false);
  const [codeQuery, setCodeQuery]     = useState("");
  const [codeResults, setCodeResults] = useState([]);
  const [codeLoading, setCodeLoading] = useState(false);
  const codeDebounce = useRef(null);

  const [selectedMake, setSelectedMake]       = useState(null);
  const [selectedModel, setSelectedModel]     = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [year, setYear]                       = useState("");

  useEffect(() => {
    setLoading(true); setError(null);
    apiFetch(`/carmakes/makes`, { method: "GET" })
      .then(r => r.json())
      .then(({ data }) => setMakes(data || []))
      .catch(() => setError("Could not load makes. Check your connection."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedMake) return;
    setLoading(true); setError(null);
    apiFetch(`/carmakes/models?make_id=${selectedMake.id}`, { method: "GET" })
      .then(r => r.json())
      .then(({ data }) => setModels(data || []))
      .catch(() => setError("Could not load models."))
      .finally(() => setLoading(false));
  }, [selectedMake]);

  useEffect(() => {
    if (!selectedModel) return;
    setLoading(true); setError(null);
    apiFetch(`/carmakes/variants?model_id=${selectedModel.id}`, { method: "GET" })
      .then(r => r.json())
      .then(({ data }) => setVariants(data || []))
      .catch(() => setError("Could not load variants."))
      .finally(() => setLoading(false));
  }, [selectedModel]);

  // Debounced model-code search
  useEffect(() => {
    clearTimeout(codeDebounce.current);
    if (codeQuery.length < 2) { setCodeResults([]); return; }
    codeDebounce.current = setTimeout(() => {
      setCodeLoading(true);
      apiFetch(`/carmakes/search?q=${encodeURIComponent(codeQuery)}`, { method: "GET" })
        .then(r => r.json())
        .then(({ data }) => setCodeResults(data || []))
        .catch(() => setCodeResults([]))
        .finally(() => setCodeLoading(false));
    }, 300);
    return () => clearTimeout(codeDebounce.current);
  }, [codeQuery]);

  const pickMake = (make) => {
    setSelectedMake(make); setSelectedModel(null); setSelectedVariant(null);
    setYear(""); setSearch(""); onAdvance(2);
  };

  const pickModel = (model) => {
    setSelectedModel(model); setSelectedVariant(null); setYear("");
    setSearch(""); onAdvance(3);
  };

  const pickVariant = (variant) => {
    setSelectedVariant(variant);
    setYear(variant.date_of_launch ? new Date(variant.date_of_launch).getFullYear().toString() : "");
    onAdvance(4);
  };

  // Model-code search result — the variant row already carries make/model from a JOIN
  const pickVariantFromCode = (result) => {
    setSelectedMake({ id: result.make_id, name: result.make_name });
    setSelectedModel({ id: result.model_id, name: result.model_name });
    setSelectedVariant(result);
    setYear(result.date_of_launch ? new Date(result.date_of_launch).getFullYear().toString() : "");
    setCodeMode(false); onAdvance(4);
  };

  const skipVariant = () => { setSelectedVariant("skip"); setYear(""); onAdvance(4); };

  const clearSearch = () => setSearch("");

  /**
   * Forget the chosen vehicle, keeping the loaded catalogue.
   *
   * For a caller that runs this flow more than once without remounting — the
   * sold-vehicle wizard's "Record another".
   *
   * NOT the thing that stops the last car's identity reaching the next one: every
   * route to step 4 already overwrites all three fields, because `pickMake` and
   * `pickModel` each clear everything downstream of themselves and all of
   * pickVariant / pickVariantFromCode / skipVariant set the year. Walking the
   * steps is what replaces the selection, and it cannot be skipped — steps 1-3
   * advance only by clicking. Do not mistake this for the safety mechanism.
   *
   * What it actually clears is the residue: the previous car's model and variant
   * LISTS, which would otherwise flash on screen at step 2 before the new fetch
   * lands, plus a stale error banner, search text and open code-search. `makes` is
   * deliberately kept — that fetch has already happened and says nothing about
   * which car this is.
   */
  const reset = () => {
    setModels([]); setVariants([]);
    setSelectedMake(null); setSelectedModel(null); setSelectedVariant(null);
    setYear(""); setSearch(""); setError(null);
    setCodeMode(false); setCodeQuery(""); setCodeResults([]);
  };

  return {
    makes, models, variants, loading, error, search, setSearch, clearSearch,
    codeMode, setCodeMode, codeQuery, setCodeQuery, codeResults, codeLoading,
    selectedMake, selectedModel, selectedVariant, year, setYear,
    pickMake, pickModel, pickVariant, pickVariantFromCode, skipVariant, reset,
  };
}

/**
 * What steps 1-4 produced, in fields.js terms. The shape both wizards feed into
 * their vehicle record.
 *
 * ONE KEY HERE IS NOT A fields.js KEY: `ModelCode`. See below.
 */
export function identityToFields(id) {
  const hasVariant = id.selectedVariant && id.selectedVariant !== "skip";
  const v = id.selectedVariant;
  return {
    Make:      id.selectedMake?.name || "",
    Model:     id.selectedModel?.name || "",
    ModelYear: id.year ? parseInt(id.year, 10) : null,
    ...(hasVariant && {
      //NOT ChassisNo, which is what this was until 2026-08-27 and is how a model
      //code came to be filed as a chassis number. `NZE141G` identifies the MODEL;
      //`NZE141G-9185659` identifies the car. fields.js asks for 8-20 characters,
      //so the short codes were caught and every code long enough to pass — most of
      //them, once the `DBA-` prefix is counted — went through silently.
      //
      //Handed out under its own name so a caller can offer it as a prefix and ask
      //for the serial that completes it. It must not reach a vehicle record: the
      //composer in SoldVehicleWizard.jsx consumes it, and buildPrefill in
      //VehiclePickerModal.jsx drops it.
      ModelCode:    sanitizeCode(v.model_code),
      Shape:        v.body_type   || "",
      EngineType:   sanitizeCode(v.engine_code),
      FuelType:     mapFuelType(v.fuel_type),
      Transmission: mapTransmission(v.transmission),
      Drive:        mapDriveType(v.driving_wheel),
      // Only when the catalogue actually has a value, so the form's own numeric
      // defaults survive an absent one.
      ...(v.displacement_cc != null && { EngineDisplacement: v.displacement_cc }),
      ...(v.doors           != null && { Doors:  v.doors }),
      ...(v.riding_capacity != null && { Seats:  v.riding_capacity }),
      ...(v.weight_kg       != null && { Weight: v.weight_kg }),
    }),
  };
}

// ── The renderer ─────────────────────────────────────────────────────────────

/** Renders whichever of steps 1-4 the parent says is current. */
export default function VehicleIdentitySteps({ step, identity: id }) {
  const filteredMakes = id.makes.filter(m => m.name.toLowerCase().includes(id.search.toLowerCase()));
  const filteredModels = id.models.filter(m => m.name.toLowerCase().includes(id.search.toLowerCase()));
  const filteredVariants = id.variants.filter(v =>
    (v.version    || "").toLowerCase().includes(id.search.toLowerCase()) ||
    (v.body_type  || "").toLowerCase().includes(id.search.toLowerCase()) ||
    (v.model_code || "").toLowerCase().includes(id.search.toLowerCase())
  );

  if (step === 1) {
    return (
      <>
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => { id.setCodeMode(false); id.setCodeQuery(""); }}
            className="flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors"
            style={!id.codeMode
              ? { background: "var(--theme-accent-tint)", borderColor: "var(--theme-accent-border)", color: "var(--theme-accent-ink)" }
              : { background: "transparent", borderColor: "var(--theme-line)", color: "var(--theme-muted)" }}
          >
            Browse by Make
          </button>
          <button
            onClick={() => id.setCodeMode(true)}
            className="flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors"
            style={id.codeMode
              ? { background: "var(--theme-accent-tint)", borderColor: "var(--theme-accent-border)", color: "var(--theme-accent-ink)" }
              : { background: "transparent", borderColor: "var(--theme-line)", color: "var(--theme-muted)" }}
          >
            Search by Model Code
          </button>
        </div>

        {id.codeMode ? (
          <>
            <SearchBox value={id.codeQuery} onChange={id.setCodeQuery} placeholder="e.g. DAA-AXVH70…" />
            <div className="overflow-y-auto max-h-64 flex flex-col gap-1 pr-1">
              {id.codeLoading && (
                <div className="text-center text-sm py-6" style={{ color: "var(--theme-muted)" }}>Searching…</div>
              )}
              {!id.codeLoading && id.codeQuery.length >= 2 && id.codeResults.length === 0 && (
                <div className="text-center text-sm py-6" style={{ color: "var(--theme-muted)" }}>No matches found</div>
              )}
              {!id.codeLoading && id.codeResults.map(r => (
                <button
                  key={r.id}
                  onClick={() => id.pickVariantFromCode(r)}
                  className="text-left px-4 py-2.5 rounded-xl border transition-all"
                  style={{ borderColor: "var(--theme-line)" }}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                >
                  <div className="text-xs mb-0.5" style={{ color: "var(--theme-muted)" }}>{r.make_name} · {r.model_name}</div>
                  <VariantRow v={r} />
                </button>
              ))}
              {!id.codeLoading && id.codeQuery.length < 2 && (
                <div className="text-center text-sm py-6" style={{ color: "var(--theme-muted)" }}>Type at least 2 characters</div>
              )}
            </div>
          </>
        ) : (
          <>
            <SearchBox value={id.search} onChange={id.setSearch} placeholder="Search makes…" />
            {id.loading ? (
              <div className="flex items-center justify-center h-48 text-sm" style={{ color: "var(--theme-muted)" }}>Loading…</div>
            ) : (
              <div className="overflow-y-auto max-h-64 grid grid-cols-2 sm:grid-cols-3 gap-2 pr-1">
                {filteredMakes.map(make => (
                  <button
                    key={make.id}
                    onClick={() => id.pickMake(make)}
                    className="text-left p-3 rounded-xl border transition-all"
                    style={{ borderColor: "var(--theme-line)" }}
                    onMouseEnter={hoverIn}
                    onMouseLeave={hoverOut}
                  >
                    <img
                      src={getMakeLogoUrl(make)}
                      alt={make.name}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      className="h-10 w-auto object-contain mx-auto mb-1"
                    />
                    <div className="font-semibold text-sm truncate" style={{ color: "var(--theme-fg)" }}>{make.name}</div>
                    {make.country_of_origin && (
                      <div className="text-xs truncate" style={{ color: "var(--theme-muted)" }}>{make.country_of_origin}</div>
                    )}
                    {make.popularity > 0 && (
                      <div className="text-xs mt-0.5" style={{ color: "var(--theme-accent-ink)" }}>{make.popularity} in stock</div>
                    )}
                  </button>
                ))}
                {filteredMakes.length === 0 && (
                  <div className="col-span-3 text-center text-sm py-8" style={{ color: "var(--theme-muted)" }}>No makes found</div>
                )}
              </div>
            )}
          </>
        )}
      </>
    );
  }

  if (step === 2) {
    return (
      <>
        <div className="text-sm mb-2" style={{ color: "var(--theme-muted)" }}>
          Make: <span className="font-semibold" style={{ color: "var(--theme-fg)" }}>{id.selectedMake?.name}</span>
        </div>
        <SearchBox value={id.search} onChange={id.setSearch} placeholder="Search models…" />
        {id.loading ? (
          <div className="flex items-center justify-center h-48 text-sm" style={{ color: "var(--theme-muted)" }}>Loading…</div>
        ) : (
          <div className="overflow-y-auto max-h-64 flex flex-col gap-1 pr-1">
            {filteredModels.map(model => (
              <button
                key={model.id}
                onClick={() => id.pickModel(model)}
                className="text-left px-4 py-2.5 rounded-xl border transition-all text-sm font-medium"
                style={{ borderColor: "var(--theme-line)", color: "var(--theme-fg)" }}
                onMouseEnter={(e) => { hoverIn(e); e.currentTarget.style.color = "var(--theme-accent-ink)"; }}
                onMouseLeave={(e) => { hoverOut(e); e.currentTarget.style.color = "var(--theme-fg)"; }}
              >
                {model.name}
              </button>
            ))}
            {filteredModels.length === 0 && (
              <div className="text-center text-sm py-8" style={{ color: "var(--theme-muted)" }}>No models found</div>
            )}
          </div>
        )}
      </>
    );
  }

  if (step === 3) {
    return (
      <>
        <div className="text-sm mb-2" style={{ color: "var(--theme-muted)" }}>
          <span className="font-semibold" style={{ color: "var(--theme-fg)" }}>{id.selectedMake?.name} {id.selectedModel?.name}</span>
        </div>
        <SearchBox value={id.search} onChange={id.setSearch} placeholder="Search variants or model codes…" />
        {id.loading ? (
          <div className="flex items-center justify-center h-48 text-sm" style={{ color: "var(--theme-muted)" }}>Loading…</div>
        ) : (
          <div className="overflow-y-auto max-h-64 flex flex-col gap-1 pr-1">
            <button
              onClick={id.skipVariant}
              className="text-left px-4 py-2.5 rounded-xl border border-dashed transition-all text-sm italic"
              style={{ borderColor: "var(--theme-line)", color: "var(--theme-muted)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--theme-accent-border)";
                e.currentTarget.style.background  = "var(--theme-surface-alt)";
              }}
              onMouseLeave={hoverOut}
            >
              None of these / skip variant
            </button>
            {filteredVariants.map(v => (
              <button
                key={v.id}
                onClick={() => id.pickVariant(v)}
                className="text-left px-4 py-2.5 rounded-xl border transition-all"
                style={{ borderColor: "var(--theme-line)" }}
                onMouseEnter={hoverIn}
                onMouseLeave={hoverOut}
              >
                <VariantRow v={v} />
              </button>
            ))}
            {filteredVariants.length === 0 && (
              <div className="text-center text-sm py-4" style={{ color: "var(--theme-muted)" }}>No variants found</div>
            )}
          </div>
        )}
      </>
    );
  }

  // step 4 — confirm the year, and show what the catalogue knows
  const hasVariant = id.selectedVariant && id.selectedVariant !== "skip";
  const v = id.selectedVariant;
  return (
    <>
      <div className="text-sm mb-4" style={{ color: "var(--theme-muted)" }}>
        <span className="font-semibold" style={{ color: "var(--theme-fg)" }}>{id.selectedMake?.name} {id.selectedModel?.name}</span>
        {hasVariant && v.version && <span style={{ color: "var(--theme-muted)" }}> · {v.version}</span>}
        {hasVariant && v.model_code && (
          <span className="font-mono text-xs ml-1" style={{ color: "var(--theme-muted)" }}>({v.model_code})</span>
        )}
      </div>
      <label className="label-t block mb-1">
        Model Year <span className="hint-t font-normal">(optional)</span>
      </label>
      <input
        type="number"
        min="1900"
        max={new Date().getFullYear() + 2}
        value={id.year}
        onChange={e => id.setYear(e.target.value)}
        placeholder="e.g. 2022"
        className="input-t w-40"
        autoFocus
      />
      {hasVariant && (
        <div
          className="mt-5 p-4 rounded-xl text-xs grid grid-cols-2 gap-x-6 gap-y-1"
          style={{ background: "var(--theme-surface-alt)", border: "1px solid var(--theme-line)", color: "var(--theme-fg)" }}
        >
          {v.body_type        && <><span style={{ color: "var(--theme-muted)" }}>Body</span><span>{v.body_type}</span></>}
          {v.engine_code      && <><span style={{ color: "var(--theme-muted)" }}>Engine Code</span><span className="font-mono">{v.engine_code}</span></>}
          {v.displacement_cc  && <><span style={{ color: "var(--theme-muted)" }}>Displacement</span><span>{v.displacement_cc} cc</span></>}
          {v.fuel_type        && <><span style={{ color: "var(--theme-muted)" }}>Fuel</span><span>{mapFuelType(v.fuel_type)}</span></>}
          {v.transmission     && <><span style={{ color: "var(--theme-muted)" }}>Transmission</span><span>{mapTransmission(v.transmission) || v.transmission}</span></>}
          {v.driving_wheel    && <><span style={{ color: "var(--theme-muted)" }}>Drive</span><span>{mapDriveType(v.driving_wheel) || v.driving_wheel}</span></>}
          {v.doors         != null && <><span style={{ color: "var(--theme-muted)" }}>Doors</span><span>{v.doors}</span></>}
          {v.riding_capacity != null && <><span style={{ color: "var(--theme-muted)" }}>Seats</span><span>{v.riding_capacity}</span></>}
          {v.weight_kg     != null && <><span style={{ color: "var(--theme-muted)" }}>Weight</span><span>{v.weight_kg} kg</span></>}
          {v.model_code       && <><span style={{ color: "var(--theme-muted)" }}>Model Code</span><span className="font-mono">{v.model_code}</span></>}
        </div>
      )}
    </>
  );
}
